import React from 'react';
import { splitRuleMarkdown } from '../utils/ruleMarkdown';

interface RuleSectionsProps {
  markdown: string;
  emptyText?: string;
}

export function RuleSections({ markdown, emptyText = 'Noch keine Regeln.' }: RuleSectionsProps) {
  const sections = splitRuleMarkdown(markdown);
  if (!sections.length) {
    return <p className="rule-folds-empty">{emptyText}</p>;
  }
  return (
    <div className="rule-folds">
      {sections.map((section, index) => (
        <details key={`${section.title}-${index}`} className="rule-fold">
          <summary>{section.title}</summary>
          {section.body ? (
            <div className="rule-fold-body">
              <MarkdownBody text={section.body} />
            </div>
          ) : null}
        </details>
      ))}
    </div>
  );
}

function MarkdownBody({ text }: { text: string }) {
  const blocks: React.ReactNode[] = [];
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  let index = 0;
  let blockKey = 0;

  while (index < lines.length) {
    const line = lines[index];
    if (!line.trim()) {
      index += 1;
      continue;
    }
    const heading = line.match(/^(#{1,3})[ \t]+(\S.*?)\s*$/);
    if (heading) {
      const Tag = heading[1].length <= 2 ? 'h3' : 'h4';
      blocks.push(
        <Tag key={blockKey} className={heading[1].length <= 2 ? 'rule-fold-section' : 'rule-fold-subsection'}>
          {heading[2]}
        </Tag>
      );
      blockKey += 1;
      index += 1;
      continue;
    }
    if (/^[-*] /.test(line)) {
      const items: string[] = [];
      while (index < lines.length && /^[-*] /.test(lines[index])) {
        items.push(lines[index].replace(/^[-*] /, ''));
        index += 1;
      }
      blocks.push(
        <ul key={blockKey}>{items.map((item, itemIndex) => <li key={itemIndex}>{renderInline(item)}</li>)}</ul>
      );
      blockKey += 1;
      continue;
    }
    if (/^\d+\. /.test(line)) {
      const items: string[] = [];
      while (index < lines.length && /^\d+\. /.test(lines[index])) {
        items.push(lines[index].replace(/^\d+\. /, ''));
        index += 1;
      }
      blocks.push(
        <ol key={blockKey}>{items.map((item, itemIndex) => <li key={itemIndex}>{renderInline(item)}</li>)}</ol>
      );
      blockKey += 1;
      continue;
    }
    const paragraph: string[] = [];
    while (
      index < lines.length
      && lines[index].trim()
      && !/^[-*] /.test(lines[index])
      && !/^\d+\. /.test(lines[index])
      && !/^#{1,3}[ \t]+\S/.test(lines[index])
    ) {
      paragraph.push(lines[index].trim());
      index += 1;
    }
    blocks.push(<p key={blockKey}>{renderInline(paragraph.join(' '))}</p>);
    blockKey += 1;
  }

  return <>{blocks}</>;
}

function renderInline(text: string): React.ReactNode[] {
  const pattern = /(\*\*([^*]+)\*\*|\*([^*]+)\*|`([^`]+)`|\[([^\]]+)\]\((https?:\/\/[^\s)]+)\))/g;
  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  let match = pattern.exec(text);
  let key = 0;
  while (match) {
    if (match.index > cursor) nodes.push(text.slice(cursor, match.index));
    if (match[2]) nodes.push(<strong key={key}>{match[2]}</strong>);
    else if (match[3]) nodes.push(<em key={key}>{match[3]}</em>);
    else if (match[4]) nodes.push(<code key={key}>{match[4]}</code>);
    else if (match[5] && match[6]) {
      nodes.push(<a key={key} href={match[6]} target="_blank" rel="noreferrer">{match[5]}</a>);
    }
    key += 1;
    cursor = match.index + match[0].length;
    match = pattern.exec(text);
  }
  if (cursor < text.length) nodes.push(text.slice(cursor));
  pattern.lastIndex = 0;
  return nodes;
}
